import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Users, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component to handle map bounds fitting
const FitBounds = ({ data }) => {
  const map = useMap();
  
  useEffect(() => {
    if (data && data.length > 0) {
      const bounds = data.map(item => [item.lat, item.lng]);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 5 });
    }
  }, [data, map]);
  
  return null;
};

const GlobalAuthorMap = () => {
  const [mapData, setMapData] = useState([]);
  const [stats, setStats] = useState({ total_countries: 0, total_authors: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMapData();
  }, []);

  const fetchMapData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/author-map-stats`);
      const result = await response.json();
      
      if (result.success) {
        setMapData(result.data);
        setStats({
          total_countries: result.total_countries,
          total_authors: result.total_authors
        });
      }
    } catch (error) {
      void 0;
    } finally {
      setLoading(false);
    }
  };

  // Calculate marker size based on author count (heatmap effect)
  const getMarkerSize = (count) => {
    if (count >= 10) return 30;
    if (count >= 5) return 20;
    if (count >= 2) return 15;
    return 10;
  };

  // Calculate marker color based on count (heatmap effect)
  const getMarkerColor = (count) => {
    if (count >= 10) return '#dc2626'; // red-600
    if (count >= 5) return '#ea580c'; // orange-600
    if (count >= 2) return '#f59e0b'; // amber-500
    return '#3b82f6'; // blue-500
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <MapPin className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-pulse" />
        <p className="text-slate-600">Loading author map...</p>
      </div>
    );
  }

  if (mapData.length === 0) {
    return (
      <div className="text-center py-20 bg-slate-50 rounded-lg">
        <MapPin className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-600">No author data available yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Title Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-slate-900 mb-3 flex items-center justify-center gap-3">
          <MapPin className="h-8 w-8 text-blue-600" />
          Global Research Community
        </h2>
        <p className="text-slate-600 mb-4">
          Authors from {stats.total_countries} countries contributing to scientific knowledge
        </p>
        <div className="flex justify-center gap-8">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            <span className="text-2xl font-bold text-slate-900">{stats.total_authors}</span>
            <span className="text-slate-600">Authors</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-600" />
            <span className="text-2xl font-bold text-slate-900">{stats.total_countries}</span>
            <span className="text-slate-600">Countries</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <Card>
        <CardContent className="p-0">
          <div style={{ height: '500px', width: '100%', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              <FitBounds data={mapData} />
              
              {mapData.map((location, index) => (
                <CircleMarker
                  key={index}
                  center={[location.lat, location.lng]}
                  radius={getMarkerSize(location.count)}
                  fillColor={getMarkerColor(location.count)}
                  color="#ffffff"
                  weight={2}
                  opacity={0.9}
                  fillOpacity={0.6}
                >
                  <Popup>
                    <div className="p-2 min-w-[200px]">
                      <h3 className="font-bold text-lg text-slate-900 mb-2 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-blue-600" />
                        {location.country}
                      </h3>
                      {location.city && (
                        <p className="text-sm text-slate-500 mb-1">{location.city}</p>
                      )}
                      <p className="text-sm text-slate-600 mb-3">
                        <span className="font-semibold">{location.count}</span> {location.count === 1 ? 'author' : 'authors'}
                      </p>
                      
                      {location.authors && location.authors.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-700 mb-1">Featured Authors:</p>
                          <ul className="space-y-1">
                            {location.authors.slice(0, 3).map((author, idx) => {
                              // Show full name if available, otherwise fallback to username
                              const displayName = (author.first_name || author.last_name) 
                                ? `${author.first_name || ''} ${author.last_name || ''}`.trim()
                                : author.username;
                              
                              return (
                                <li key={idx} className="text-xs text-slate-600">
                                  • {displayName} - {author.occupation}
                                </li>
                              );
                            })}
                          </ul>
                          {location.count > 3 && (
                            <p className="text-xs text-slate-500 mt-1">
                              + {location.count - 3} more...
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#3b82f6' }}></div>
          <span className="text-slate-600">1-2 authors</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#f59e0b' }}></div>
          <span className="text-slate-600">2-5 authors</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#ea580c' }}></div>
          <span className="text-slate-600">5-10 authors</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#dc2626' }}></div>
          <span className="text-slate-600">10+ authors</span>
        </div>
      </div>
    </div>
  );
};

export default GlobalAuthorMap;
